import type { CSSProperties } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Brand spinner: a purple arc on a faint track. Falls back to a gentle
 * pulse when the user prefers reduced motion (the accompanying label
 * still says "Loading").
 */
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-4 w-4 rounded-full border-2 border-primary/25 border-t-primary",
        "motion-safe:animate-spin motion-reduce:animate-pulse",
        className,
      )}
    />
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      role="status"
      className="flex h-64 items-center justify-center gap-2.5 text-sm text-muted-foreground"
    >
      <Spinner />
      {label}
    </div>
  );
}

/** Shimmering placeholder block; size it with h-* / w-* classes. */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return <div aria-hidden className={cn("skeleton", className)} style={style} />;
}

/**
 * Placeholder for a chart while its data loads. Ranked skeleton bars keep
 * the card at its final height so nothing jumps when the data lands.
 */
export function ChartSkeleton({ height = 240 }: { height?: number }) {
  // Fixed pseudo-ranked widths — deliberate, not random, so the layout is
  // stable across re-renders.
  const widths = [92, 78, 64, 55, 43, 34, 26];
  const rows = Math.max(3, Math.min(widths.length, Math.floor(height / 36)));
  return (
    <div
      role="status"
      aria-label="Loading chart"
      className="flex flex-col justify-around"
      style={{ height }}
    >
      {widths.slice(0, rows).map((w) => (
        <Skeleton key={w} className="h-4" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/** Placeholder table: header line plus shimmering rows. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading table"
      className="flex flex-col gap-3 rounded-md border p-3"
    >
      <Skeleton className="h-4 w-1/3" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-4 w-full" />
      ))}
    </div>
  );
}

export function ChartEmptyState({
  message = "No data for this period",
}: {
  message?: string;
}) {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3">
      <p className="text-sm text-muted-foreground">
        Something went wrong: {message}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
